// This optional code is used to register a service worker.
// register() is not called by default.

import { Workbox } from 'workbox-window';

// This lets the app know when the user is running in a PWA environment
export const isRunningAsPWA = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.matchMedia('(display-mode: minimal-ui)').matches ||
         (window.navigator as any).standalone === true;
};

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Function to register the service worker
export function register(): { serviceWorker: Workbox | null } {
  // Check if the service worker API is available
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);

    // Make sure we're not registering a service worker for a different origin
    if (publicUrl.origin !== window.location.origin) {
      return { serviceWorker: null };
    }

    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
    const wb = new Workbox(swUrl);

    wb.addEventListener('activated', (event) => {
      // Ensure the service worker is activated
      console.log('Service worker activated:', event);
    });

    wb.addEventListener('waiting', (event) => {
      // A new service worker is waiting to be activated
      // We'll show a notification to the user
      console.log('Service worker is waiting to be activated:', event);
      triggerUpdateNotification(wb);
    });

    wb.addEventListener('controlling', (event) => {
      // The service worker is now controlling the page
      // We'll reload the page to ensure the new version is used
      window.location.reload();
    });

    // Register the service worker - different method based on localhost or production
    if (isLocalhost) {
      // This is running on localhost. Let's check if a service worker still exists or not.
      checkValidServiceWorker(swUrl, wb);
    } else {
      // Is not localhost. Just register service worker
      wb.register()
        .then((registration) => {
          console.log('Service worker registered:', registration);
        })
        .catch((error) => {
          console.error('Error during service worker registration:', error);
        });
    }

    return { serviceWorker: wb };
  }
  return { serviceWorker: null };
}

function checkValidServiceWorker(swUrl: string, wb: Workbox) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        wb.register()
          .then((registration) => {
            console.log('Service worker registered:', registration);
          })
          .catch((error) => {
            console.error('Error during service worker registration:', error);
          });
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

// Function to trigger the update notification
export function triggerUpdateNotification(wb: Workbox): void {
  // Create and dispatch a custom event for the update notification
  const updateAvailableEvent = new CustomEvent('updateAvailable', {
    detail: { serviceWorker: wb }
  });
  window.dispatchEvent(updateAvailableEvent);
}

// Function to apply the update
export function applyUpdate(wb: Workbox): void {
  // Send a message to the service worker to skip waiting
  wb.messageSkipWaiting();
}

// Function to unregister the service worker (if needed)
export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
} 